import React, { useEffect, useRef, useState } from "react";

// ============================================================================
// Premium cursor — dual-state amber reticle with hover label.
// Magnetic feel via spring-eased lerp. Click ripple on press.
// ============================================================================

export default function Cursor() {
    const ringRef = useRef(null);
    const dotRef = useRef(null);
    const labelRef = useRef(null);
    const [enabled, setEnabled] = useState(true);
    const [hoverLabel, setHoverLabel] = useState("");

    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const coarse = window.matchMedia("(pointer: coarse)").matches;
        if (reduced || coarse) { setEnabled(false); document.body.style.cursor = "auto"; return; }

        let raf = 0;
        let mx = window.innerWidth / 2, my = window.innerHeight / 2;
        let rx = mx, ry = my;
        let dx = mx, dy = my;
        let pressed = false;
        let hovering = null;

        const onMove = (e) => { mx = e.clientX; my = e.clientY; };

        const onOver = (e) => {
            const t = e.target.closest("a, button, [role='button'], input, textarea, [data-cursor-label]");
            if (!t) {
                hovering = null;
                setHoverLabel("");
                document.body.classList.remove("cursor-hover");
                return;
            }
            hovering = t;
            const label = t.getAttribute("data-cursor-label") ||
                (t.tagName === "A" || t.tagName === "BUTTON" ? "CLICK" : "TYPE");
            setHoverLabel(label);
            document.body.classList.add("cursor-hover");
        };

        const onDown = (e) => {
            pressed = true;
            // Ripple
            const r = document.createElement("span");
            r.className = "ripple";
            r.style.left = e.clientX + "px";
            r.style.top = e.clientY + "px";
            r.style.width = "4px"; r.style.height = "4px";
            r.style.marginLeft = "-2px"; r.style.marginTop = "-2px";
            r.style.position = "fixed";
            r.style.zIndex = "199";
            document.body.appendChild(r);
            setTimeout(() => r.remove(), 600);
        };
        const onUp = () => { pressed = false; };

        const tick = () => {
            // Magnetic pull when hovering an interactive element
            let targetX = mx, targetY = my;
            if (hovering) {
                const rect = hovering.getBoundingClientRect();
                const cx = rect.left + rect.width / 2;
                const cy = rect.top + rect.height / 2;
                const dist = Math.hypot(mx - cx, my - cy);
                const max = Math.min(rect.width, rect.height) * 0.6;
                if (dist < max) {
                    const pull = 1 - dist / max;
                    targetX = mx + (cx - mx) * pull * 0.25;
                    targetY = my + (cy - my) * pull * 0.25;
                }
            }

            // Ring: slower lerp
            rx += (targetX - rx) * 0.18;
            ry += (targetY - ry) * 0.18;
            // Dot: faster lerp
            dx += (mx - dx) * 0.45;
            dy += (my - dy) * 0.45;

            if (ringRef.current) {
                const scale = (hovering ? 1.35 : 1) * (pressed ? 0.85 : 1);
                ringRef.current.style.transform = `translate3d(${rx - 16}px, ${ry - 16}px, 0) scale(${scale})`;
            }
            if (dotRef.current) {
                dotRef.current.style.transform = `translate3d(${dx - 2}px, ${dy - 2}px, 0)`;
                dotRef.current.style.opacity = hovering ? "0" : "1";
            }
            if (labelRef.current) {
                labelRef.current.style.transform = `translate3d(${rx + 22}px, ${ry + 4}px, 0)`;
            }
            raf = requestAnimationFrame(tick);
        };

        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("mouseover", onOver, { passive: true });
        window.addEventListener("mousedown", onDown, { passive: true });
        window.addEventListener("mouseup", onUp, { passive: true });
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseover", onOver);
            window.removeEventListener("mousedown", onDown);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    if (!enabled) return null;
    return (
        <>
            <div
                ref={ringRef}
                className="pointer-events-none fixed top-0 left-0 z-[200] w-8 h-8 rounded-full mix-blend-difference"
                style={{
                    border: "1.2px solid rgba(255,176,0,0.85)",
                    transition: "transform 0.04s linear, scale 0.3s var(--ease-os)",
                    boxShadow: "0 0 12px rgba(255,176,0,0.25)",
                }}
            />
            <div
                ref={dotRef}
                className="pointer-events-none fixed top-0 left-0 z-[200] w-1 h-1 rounded-full"
                style={{ background: "var(--signal)", transition: "opacity 0.2s var(--ease-os)" }}
            />
            {hoverLabel && (
                <div
                    ref={labelRef}
                    className="pointer-events-none fixed top-0 left-0 z-[200] font-mono text-[9px] uppercase tracking-[0.3em] text-signal whitespace-nowrap"
                    style={{ textShadow: "0 0 6px rgba(0,0,0,0.9)" }}
                >
                    › {hoverLabel}
                </div>
            )}
        </>
    );
}
